import {
  Grid,
  Typography,
  Card,
  Box,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  useMediaQuery,
  Theme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import OpenInNewIcon from "@mui/icons-material/OpenInNewRounded";
import InfoRow from "../../../components/Common/InfoRow";
import { explorerTxUrl, formatCrypto, formatDateTime } from "../../../utils/paymentFormat";

// A ledger type as read (refund_pending → Refund pending).
const typeLabel = (type?: string) => {
  const text = String(type || "").replace(/_/g, " ").toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const ExplorerLink = ({ payment }) => {
  const { t } = useTranslation();
  const url = explorerTxUrl(payment.currency, payment.txId);
  if (!url) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Tooltip title={t("Open in explorer")}>
      <IconButton size="small" href={url} target="_blank" rel="noopener noreferrer" aria-label={t("Open in explorer")}>
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const MobileCard = ({ payment }) => {
  const { t } = useTranslation();
  return (
    <Card sx={{ mb: 2 }}>
      <Box px={2} py={1}>
        <InfoRow label={t("Date")}>{formatDateTime(payment.dateCreated)}</InfoRow>
        <InfoRow label={t("Type")}>{typeLabel(payment.type)}</InfoRow>
        <InfoRow label={t("Amount")}>
          {formatCrypto(payment.amount)} {payment.currency}
        </InfoRow>
        <InfoRow label={t("Credit")}>{formatCrypto(payment.credit)}</InfoRow>
        <InfoRow label={t("Debit")}>{formatCrypto(payment.debit)}</InfoRow>
        <InfoRow label={t("Explorer")}>
          <ExplorerLink payment={payment} />
        </InfoRow>
      </Box>
    </Card>
  );
};

const TablePayments = ({ payments }: { payments: any[] }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));

  if (payments.length === 0) return null;

  if (isMobile) {
    return (
      <Grid item xs={12}>
        {payments.map((payment) => (
          <MobileCard key={payment.id} payment={payment} />
        ))}
      </Grid>
    );
  }

  const number = { fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } as const;
  return (
    <Grid item xs={12}>
      <Box pb={1.5}>
        <Typography variant="h6">{t("Payments")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Deposits, refunds and sweeps recorded on this transaction.")}
        </Typography>
      </Box>
      <Card>
        <TableContainer sx={{ overflowX: "auto", "& .MuiTableCell-root": { px: 1.5 }, "& .MuiTableCell-root:first-of-type": { pl: 2 }, "& .MuiTableCell-root:last-of-type": { pr: 1.5 } }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("Date")}</TableCell>
                <TableCell>{t("Type")}</TableCell>
                <TableCell align="right">{t("Amount")}</TableCell>
                <TableCell align="right">{t("Credit")}</TableCell>
                <TableCell align="right">{t("Debit")}</TableCell>
                <TableCell align="right">{t("Explorer")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(payment.dateCreated)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{typeLabel(payment.type)}</TableCell>
                  <TableCell align="right" sx={number}>
                    {formatCrypto(payment.amount)} {payment.currency}
                  </TableCell>
                  <TableCell align="right" sx={number}>{formatCrypto(payment.credit)}</TableCell>
                  <TableCell align="right" sx={number}>{formatCrypto(payment.debit)}</TableCell>
                  <TableCell align="right">
                    <ExplorerLink payment={payment} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Grid>
  );
};

export default TablePayments;
